import React, { useState, useEffect } from 'react';
import styles from '../styles/DateOfBirthField.module.css';

const DateOfBirthField = ({ value, onChange, required = false, className = '', style = {} }) => {
  // Parse existing value if provided (format: YYYY-MM-DD)
  const parseDate = (dateString) => {
    if (!dateString) return { day: '', month: '', year: '' };
    const [year, month, day] = dateString.split('-');
    return {
      day: day ? parseInt(day, 10) : '',
      month: month ? parseInt(month, 10) : '',
      year: year || ''
    };
  };

  const [dateFields, setDateFields] = useState(parseDate(value));

  // Update internal state when external value changes
  useEffect(() => {
    if (value && value !== formatToYYYYMMDD(dateFields)) {
      setDateFields(parseDate(value));
    }
  }, [value]);

  // Format date to YYYY-MM-DD for backend
  const formatToYYYYMMDD = (fields) => {
    if (!fields.day || !fields.month || !fields.year) return '';
    const paddedMonth = String(fields.month).padStart(2, '0');
    const paddedDay = String(fields.day).padStart(2, '0');
    return `${fields.year}-${paddedMonth}-${paddedDay}`;
  };

  // Get number of days in a month
  const getDaysInMonth = (month, year) => {
    if (!month) return 31;
    if (!year) year = 2000; // Use leap year for Feb if year not selected
    return new Date(year, month, 0).getDate();
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    const newFields = { ...dateFields, [field]: value };
    
    // Reset day if it exceeds the new month's max days (handles leap years automatically)
    if (field === 'month' || field === 'year') {
      const maxDays = getDaysInMonth(newFields.month, newFields.year);
      if (newFields.day > maxDays) {
        newFields.day = ''; // Reset day dropdown instead of auto-adjusting
      }
    }

    setDateFields(newFields);

    // Call onChange with formatted date string
    const formattedDate = formatToYYYYMMDD(newFields);
    if (onChange) {
      onChange({ target: { name: 'dob', value: formattedDate } });
    }
  };

  // Generate day options (1-31, adjusted based on selected month)
  const maxDays = getDaysInMonth(dateFields.month, dateFields.year);
  const dayOptions = Array.from({ length: maxDays }, (_, i) => i + 1);

  // Month options (with names)
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Get current year for validation
  const currentYear = new Date().getFullYear();

  // Handle year input with validation
  const handleYearChange = (e) => {
    let yearValue = e.target.value.replace(/\D/g, ''); // Only digits
    if (yearValue.length > 4) yearValue = yearValue.slice(0, 4);

    // Once a full 4-digit year is typed, clamp it into a sane range:
    // no future years, and no more than 100 years back from today.
    if (yearValue.length === 4) {
      const parsedYear = parseInt(yearValue, 10);
      const minYear = currentYear - 100;
      if (parsedYear > currentYear) {
        yearValue = String(currentYear);
      } else if (parsedYear < minYear) {
        yearValue = String(minYear);
      }
    }

    handleFieldChange('year', yearValue);
  };

  return (
    <div className={`${styles.dobContainer} ${className}`} style={style}>
      <div className={styles.dobFields}>
        <div className={styles.dobField}>
          <select
            value={dateFields.day}
            onChange={(e) => handleFieldChange('day', e.target.value ? parseInt(e.target.value, 10) : '')}
            className={styles.dobSelect}
            required={required}
            aria-label="Day"
          >
            <option value="">Day</option>
            {dayOptions.map(day => (
              <option key={day} value={day}>{day}</option>
            ))}
          </select>
        </div>

        <div className={styles.dobField}>
          <select
            value={dateFields.month}
            onChange={(e) => handleFieldChange('month', e.target.value ? parseInt(e.target.value, 10) : '')}
            className={styles.dobSelect}
            required={required}
            aria-label="Month"
          >
            <option value="">Month</option>
            {monthOptions.map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </div>

        <div className={styles.dobField}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={dateFields.year}
            onChange={handleYearChange}
            placeholder="YYYY"
            className={styles.dobInput}
            required={required}
            maxLength={4}
            aria-label="Year"
          />
        </div>
      </div>
      
      {dateFields.day && dateFields.month && dateFields.year && (
        <div className={styles.dobPreview}>
          Selected: {dateFields.day} {monthOptions[dateFields.month - 1]?.label} {dateFields.year}
        </div>
      )}
    </div>
  );
};

export default DateOfBirthField;
